/**
 * Safe MedMic/CRE token diagnostics — never log raw JWTs.
 * Verbose logs are off in production unless REACT_APP_MEDMIC_AUTH_DEBUG=true.
 */

function isMedmicAuthDebugEnabled() {
  try {
    if (typeof process !== "undefined" && process.env?.REACT_APP_MEDMIC_AUTH_DEBUG === "true") {
      return true;
    }
    if (typeof process !== "undefined" && process.env?.NODE_ENV === "development") {
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

/**
 * @param {string | null | undefined} token
 * @returns {{
 *   present: boolean,
 *   fingerprint: string | null,
 *   aud: string | null,
 *   iss: string | null,
 *   tid: string | null,
 *   iat: number | null,
 *   exp: number | null,
 *   expiresAtMs: number | null,
 *   scp: string | null,
 * }}
 */
export function getSafeTokenMeta(token) {
  if (!token || typeof token !== "string") {
    return {
      present: false,
      fingerprint: null,
      aud: null,
      iss: null,
      tid: null,
      iat: null,
      exp: null,
      expiresAtMs: null,
      scp: null,
    };
  }

  const parts = token.split(".");
  if (parts.length < 2) {
    return {
      present: true,
      fingerprint: fingerprint(token),
      aud: null,
      iss: null,
      tid: null,
      iat: null,
      exp: null,
      expiresAtMs: null,
      scp: null,
    };
  }

  try {
    const payload = JSON.parse(base64UrlDecode(parts[1]));
    const exp =
      typeof payload.exp === "number" ? payload.exp : null;
    return {
      present: true,
      fingerprint: fingerprint(token),
      aud: stringifyClaim(payload.aud),
      iss: typeof payload.iss === "string" ? payload.iss : null,
      tid: typeof payload.tid === "string" ? payload.tid : null,
      iat: typeof payload.iat === "number" ? payload.iat : null,
      exp,
      expiresAtMs: exp != null ? exp * 1000 : null,
      scp:
        typeof payload.scp === "string"
          ? payload.scp
          : Array.isArray(payload.roles)
            ? payload.roles.join(" ")
            : null,
    };
  } catch {
    return {
      present: true,
      fingerprint: fingerprint(token),
      aud: null,
      iss: null,
      tid: null,
      iat: null,
      exp: null,
      expiresAtMs: null,
      scp: null,
    };
  }
}

/**
 * @param {string} surface
 * @param {string} event
 * @param {Record<string, unknown>} [fields]
 */
export function logMedmicAuthDiag(surface, event, fields = {}) {
  if (!isMedmicAuthDebugEnabled()) return;
  try {
    // eslint-disable-next-line no-console
    console.info("[medmic-auth]", {
      surface,
      event,
      ...fields,
    });
  } catch {
    /* ignore */
  }
}

function fingerprint(token) {
  const head = token.slice(0, 8);
  const tail = token.slice(-6);
  return `${head}…${tail}:${token.length}`;
}

function stringifyClaim(value) {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map(String).join(",");
  return null;
}

function base64UrlDecode(segment) {
  const padded = segment + "=".repeat((4 - (segment.length % 4)) % 4);
  const b64 = padded.replace(/-/g, "+").replace(/_/g, "/");
  if (typeof atob !== "function") {
    throw new Error("base64_decode_unavailable");
  }
  return atob(b64);
}
